import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { Switch } from '@mui/material';
import { ThrillIcon } from '@repo/ui/Icon';
import { checkIsLogin } from '@repo/utils';
import styled, { useTheme } from 'styled-components';
import { aw } from 'vitest/dist/chunks/reporters.nr4dxCkA.js';

import { useGetEnableNsfw, useGetMyProfile, useUpdateNsfwDisable, useUpdateNsfwEnable } from 'services/hooks/user';
import { openModal } from 'store/modalStackStore';

import MinorRestrictionModalBody from 'components/modal/MinorRestrictionModalBody';
import { OutletChildType } from 'components/provider/GlobalProvider';
type Props = {
  handleClick?: () => void;
};

const AdultToggleButton = ({ handleClick }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { device } = useOutletContext<OutletChildType>();
  const { data: userProfile } = useGetMyProfile();

  const isLogin = checkIsLogin();

  const { data: enableNsfwData } = useGetEnableNsfw({ enabled: !!isLogin });
  const { mutateAsync: updateNsfwEnable, isLoading: nsfwEnableLoading } = useUpdateNsfwEnable(userProfile?.id);
  const { mutateAsync: updateNsfwDisable, isLoading: nsfwDisableLoading } = useUpdateNsfwDisable(userProfile?.id);

  const isAdult = enableNsfwData?.isAdult;
  const enableNsfw = enableNsfwData?.enableNsfw;

  const handleChangeAdult = async () => {
    if (!isAdult) {
      openModal(<MinorRestrictionModalBody />, {
        modalWrapperStyle: {
          padding: 0,
          width: !device.isMobile ? '100%' : '400px',
          maxWidth: !device.isMobile ? '400' : 'initial',
          borderRadius: !device.isMobile ? '0px' : '12px',
        },
      });
      return;
    }

    try {
      if (enableNsfw) {
        await updateNsfwDisable();
      } else {
        await updateNsfwEnable();
      }
      handleClick?.();
    } catch (e) {}
  };

  return (
    <SLabel>
      {device.isLaptop ? <span>{t('chat.home_nsfw_toggle')}</span> : <span>{t('chat.home_nsfw_toggle_mobile')}</span>}
      <SThrillSwitchButton
        checked={enableNsfw}
        icon={
          <ThrillIcon
            style={{
              boxShadow: '0px 1px 1px 0px rgba(0, 0, 0, 0.06), 0px 3px 8px 0px rgba(0, 0, 0, 0.15)',
            }}
          />
        }
        checkedIcon={
          <ThrillIcon
            style={{
              boxShadow: '0px 1px 1px 0px rgba(0, 0, 0, 0.06), 0px 3px 8px 0px rgba(0, 0, 0, 0.15)',
            }}
          />
        }
        onChange={handleChangeAdult}
        disableRipple
        disabled={nsfwEnableLoading || nsfwDisableLoading}
        sx={{
          overflow: 'initial',
          '& .MuiSwitch-track': {
            background: theme.color.buttons.default,
          },
        }}
      />
    </SLabel>
  );
};

const SLabel = styled.label`
  display: flex;
  gap: 4px;
  align-items: center;
  cursor: pointer;

  & > span {
    color: ${({ theme }) => theme.color.static.adult};
    z-index: 1;
    ${({ theme }) => theme.font.body4_sb};
    cursor: pointer;
  }
`;

const SThrillSwitchButton = styled(Switch)(({ theme }) => ({
  width: '40px !important',
  padding: '12px 0px !important',
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translate(-1px, 6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translate(15px, 6px)',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.color.static.adult,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: '#D8D8D8',
    borderRadius: 20 / 2,
  },
}));

export default AdultToggleButton;
